import React from 'react';
import { Button, Form, Input, Space, Typography } from 'antd';
import { InfoCircleOutlined, MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';

const SUFFIX_COLOR = '#8b2942';
const CHEVRON = '#c62828';

function PrathamaLine({ segmentName }: { segmentName: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
      <Typography.Text type="secondary" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
        प्रथमैकवचने
      </Typography.Text>
      <span style={{ color: CHEVRON, fontWeight: 600 }}>»</span>
      <Space size={[4, 8]} wrap style={{ flex: 1 }}>
        <Space size={4} align="center" wrap>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            (पुं)
          </Typography.Text>
          <span style={{ color: CHEVRON }}>-</span>
          <Form.Item name={[segmentName, 'pu']} style={{ marginBottom: 0 }}>
            <Input style={{ minWidth: 100 }} />
          </Form.Item>
        </Space>
        <Typography.Text type="secondary">|</Typography.Text>
        <Space size={4} align="center" wrap>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            (स्त्री)
          </Typography.Text>
          <span style={{ color: CHEVRON }}>-</span>
          <Form.Item name={[segmentName, 'st']} style={{ marginBottom: 0 }}>
            <Input style={{ minWidth: 100 }} />
          </Form.Item>
        </Space>
        <Typography.Text type="secondary">|</Typography.Text>
        <Space size={4} align="center" wrap>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            (नपुं)
          </Typography.Text>
          <span style={{ color: CHEVRON }}>-</span>
          <Form.Item name={[segmentName, 'na']} style={{ marginBottom: 0 }}>
            <Input style={{ minWidth: 100 }} />
          </Form.Item>
        </Space>
        <Typography.Text type="secondary">।</Typography.Text>
      </Space>
    </div>
  );
}

/**
 * `key` lives at `pairs[i].key` and must NOT be rendered inside
 * `Form.List name={[i, 'segments']}` — otherwise rc-form binds it under
 * `pairs[i].segments.*` and values stay empty.
 */
function KrutPratyayaCard({ name: pairIndex, onRemove }: { name: number; onRemove: () => void }) {
  return (
    <div style={{ borderBottom: '1px solid #d9d9d9', paddingBottom: 20, marginBottom: 16 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          marginBottom: 12,
          flexWrap: 'wrap',
        }}
      >
        <InfoCircleOutlined style={{ color: '#8c8c8c', marginTop: 8, flexShrink: 0 }} />
        <Form.Item name={[pairIndex, 'key']} style={{ marginBottom: 0, flex: '1 1 140px', minWidth: 100 }}>
          <Input
            variant="borderless"
            placeholder="ल्युट् …"
            style={{
              color: SUFFIX_COLOR,
              fontWeight: 700,
              fontSize: 16,
            }}
          />
        </Form.Item>
        <Button
          type="text"
          danger
          size="small"
          icon={<MinusCircleOutlined />}
          onClick={onRemove}
          style={{ marginLeft: 'auto' }}
        >
          Remove
        </Button>
      </div>

      <Form.List name={[pairIndex, 'segments']}>
        {(segFields, { add: addSeg, remove: remSeg }) => {
          const single = segFields.length === 1;
          return (
            <>
              {segFields.map((sf) => (
                <div key={sf.key} style={{ marginBottom: 12 }}>
                  {single ? (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          flexWrap: 'wrap',
                          marginBottom: 8,
                          marginLeft: 28,
                        }}
                      >
                        <span style={{ color: CHEVRON, fontSize: 18, fontWeight: 600 }}>»</span>
                        <Form.Item name={[sf.name, 'base']} style={{ marginBottom: 0, flex: '1 1 160px', minWidth: 120 }}>
                          <Input placeholder="भवन …" style={{ fontWeight: 600 }} />
                        </Form.Item>
                      </div>
                      <div style={{ marginLeft: 28 }}>
                        <PrathamaLine segmentName={sf.name} />
                      </div>
                    </>
                  ) : (
                    <div
                      style={{
                        marginLeft: 28,
                        paddingLeft: 14,
                        borderLeft: `3px solid ${CHEVRON}`,
                        marginBottom: 14,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                        <span style={{ color: CHEVRON, fontSize: 18, fontWeight: 600 }}>»</span>
                        <Form.Item name={[sf.name, 'base']} style={{ marginBottom: 0, flex: 1, minWidth: 140 }}>
                          <Input placeholder="भवन …" style={{ fontWeight: 600 }} />
                        </Form.Item>
                        <Button type="link" size="small" danger onClick={() => remSeg(sf.name)}>
                          Remove group (;)
                        </Button>
                      </div>
                      <PrathamaLine segmentName={sf.name} />
                    </div>
                  )}
                </div>
              ))}

              <Button
                type="dashed"
                size="small"
                onClick={() => addSeg({ base: '', pu: '', st: '', na: '' })}
                style={{ marginLeft: 28 }}
                icon={<PlusOutlined />}
              >
                Add Row
              </Button>
            </>
          );
        }}
      </Form.List>
    </div>
  );
}

/** Form.List name must be `pairs` — items: { key, segments: { base, pu, st, na }[] }. */
export function TingantaKrutPratyayaFormList() {
  return (
    <Form.List name="pairs">
      {(fields, { add, remove }) => (
        <>
          {fields.map(({ key, name }) => (
            <KrutPratyayaCard key={key} name={name} onRemove={() => remove(name)} />
          ))}
          <Form.Item>
            <Button
              type="dashed"
              onClick={() =>
                add({
                  key: '',
                  segments: [{ base: '', pu: '', st: '', na: '' }],
                })
              }
              block
              icon={<PlusOutlined />}
            >
              Add pratyaya
            </Button>
          </Form.Item>
        </>
      )}
    </Form.List>
  );
}
